import { translate } from "@/i18n";
import { call, post } from "@/services/api";

// Photos and videos of this app's records, stored in s3://myotaku-warehouse. The file goes STRAIGHT
// from this browser to S3 through a signed link — it never passes through the app, and the staff
// token is never sent to S3. docs/decisions/media-storage.md.
export type Attachable = "stock_piece" | "issue" | "run" | "supplier_receipt" | "recognition" | "cart";
export type MediaFile = {
  id: number; kind: "image" | "video" | "document"; content_type: string | null; byte_size: number | null;
  width: number | null; height: number | null; original_filename: string | null;
  uploaded_at: string; uploaded_by: string | null;
  // What a stock piece's photo shows (his captions); null elsewhere.
  role?: "identification" | "damage" | "wrapped" | null;
  // One furniture line of a job ("line:12" / "item:34"), or the job as a whole (null).
  item_key?: string | null;
  url: string | null;
  // null while the thumbnails are being made — the screen shows the original until then.
  thumb_url: string | null;
};

const TYPES_BY_EXTENSION: Record<string, string> = {
  jpg: "image/jpeg", jpeg: "image/jpeg", png: "image/png", webp: "image/webp", heic: "image/heic",
  heif: "image/heif", mp4: "video/mp4", mov: "video/quicktime", webm: "video/webm", pdf: "application/pdf",
};

// Some phones hand an iPhone photo (HEIC) to the browser with an empty type: the extension decides then.
const contentTypeOf = (file: File) =>
  file.type || TYPES_BY_EXTENSION[file.name.split(".").pop()?.toLowerCase() ?? ""] || "";

// His editors' "Delete" — admins only, on the server too.
export const deleteMedia = (id: number) => call<{ deleted: number }>(`/api/v1/media_files/${id}`, { method: "DELETE" });

// `itemKey`: a string for one line's files, null for the job's own, left out for every file.
export const fetchMedia = (type: Attachable, id: number, itemKey?: string | null) =>
  call<{ media: MediaFile[] }>(`/api/v1/media_files?attachable_type=${type}&attachable_id=${id}${itemKey === undefined ? "" : `&item_key=${encodeURIComponent(itemKey ?? "")}`}`);

export async function uploadMedia(type: Attachable, id: number, file: File, role?: MediaFile["role"], itemKey?: string | null): Promise<MediaFile> {
  const contentType = contentTypeOf(file);
  const started = await post<{ media: { id: number }; upload: { method: string; url: string; headers: Record<string, string> } }>(
    "/api/v1/media_files",
    { attachable_type: type, attachable_id: id,
      kind: contentType.startsWith("video/") ? "video" : contentType === "application/pdf" ? "document" : "image",
      content_type: contentType, filename: file.name, role: role ?? null, item_key: itemKey ?? null },
  );
  try {
    const put = await fetch(started.upload.url, { method: started.upload.method, headers: started.upload.headers, body: file });
    // `refused`: the storage's own answer, which a job's strip says with his one sentence (MediaStrip).
    if (!put.ok) throw Object.assign(new Error(translate("error.upload", { status: put.status })), { refused: put.status });
    return await post<MediaFile>(`/api/v1/media_files/${started.media.id}/confirm`);
  } catch (e) {
    // His `mutate` records a file only once it is up: one that did not reach the storage, or was not
    // confirmed, leaves nothing — its pending row goes at once. A delete that cannot be made (no network,
    // or nothing left to delete) is the server's sweep's to finish; the failure said is the upload's. Only
    // a pending row: a confirm that went through with its answer lost keeps its file (pending_only).
    await call(`/api/v1/media_files/${started.media.id}?pending_only=1`, { method: "DELETE" }).catch(() => {});
    throw e;
  }
}
